/**
 * TodoQuickAddBar - Bottom input bar for rapid task entry.
 * Keeps keyboard open after submission for continuous thought capture.
 * Includes a high-priority flag toggle to the right of the input.
 */

import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View, type NativeSyntheticEvent, type TextInputContentSizeChangeEventData } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withSequence, withTiming } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { IconSymbol } from '@/components/ui/icon-symbol';
import { TodoVoiceButton } from './TodoVoiceButton';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

const MAX_LINES = 5;
const LINE_HEIGHT = 20;
const BASE_HEIGHT = 24;

export type TodoQuickAddBarHandle = {
  focusAndFlash: () => void;
};

type TodoQuickAddBarProps = {
  onAddTask: (title: string, priority?: 'high' | null) => void;
};

export const TodoQuickAddBar = React.forwardRef<TodoQuickAddBarHandle, TodoQuickAddBarProps>(function TodoQuickAddBar({ onAddTask }, ref) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [text, setText] = useState('');
  const [highPriority, setHighPriority] = useState(false);
  const [inputHeight, setInputHeight] = useState(BASE_HEIGHT);
  const inputRef = useRef<TextInput>(null);
  const submittingRef = useRef(false);
  const hasText = text.trim().length > 0;
  const flashOpacity = useSharedValue(0);

  useImperativeHandle(ref, () => ({
    focusAndFlash: () => {
      inputRef.current?.focus();
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      flashOpacity.value = withSequence(
        withTiming(1, { duration: 100 }),
        withTiming(0, { duration: 400 }),
      );
    },
  }), [flashOpacity]);

  const flashStyle = useAnimatedStyle(() => ({
    opacity: flashOpacity.value,
  }));

  const handleContentSizeChange = useCallback(
    (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
      const { height } = event.nativeEvent.contentSize;
      const maxHeight = BASE_HEIGHT + LINE_HEIGHT * (MAX_LINES - 1);
      setInputHeight(Math.min(Math.max(BASE_HEIGHT, height), maxHeight));
    },
    [],
  );

  const handleSubmit = useCallback((overrideText?: string) => {
    // Guard against duplicate onChangeText fires (e.g. multiline Enter
    // dispatching change events more than once before state can settle).
    if (submittingRef.current) return;
    const trimmed = (overrideText ?? text).trim();
    if (!trimmed) return;
    submittingRef.current = true;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onAddTask(trimmed, highPriority ? 'high' : null);
    setText('');
    setHighPriority(false);
    setInputHeight(BASE_HEIGHT);
    // Release on next tick so the next user-initiated submit works.
    setTimeout(() => { submittingRef.current = false; }, 0);
    // Keep keyboard open for rapid entry
  }, [text, highPriority, onAddTask]);

  const handleTogglePriority = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setHighPriority(prev => !prev);
  }, []);

  const handleVoiceTranscribed = useCallback((transcribed: string) => {
    setText(transcribed);
    inputRef.current?.focus();
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: palette.cardBackground, borderColor: palette.primary }]}>
      <Animated.View
        style={[styles.flashOverlay, { backgroundColor: palette.primary }, flashStyle]}
        pointerEvents="none"
      />
      <TodoVoiceButton
        accentColor={palette.primary}
        onTranscribed={handleVoiceTranscribed}
      />

      <TextInput
        ref={inputRef}
        style={[styles.input, { color: palette.text, height: inputHeight }]}
        placeholder="Add a task..."
        placeholderTextColor={palette.textMuted}
        value={text}
        onContentSizeChange={handleContentSizeChange}
        onChangeText={(val) => {
          if (val.includes('\n')) {
            // Source the title from the event value (not React state) so a
            // pasted "title\n" submits correctly on the very first event.
            handleSubmit(val.split('\n')[0]);
            return;
          }
          setText(val);
        }}
        blurOnSubmit={false}
        multiline
        textAlignVertical="top"
        autoCorrect
      />

      <Pressable onPress={handleTogglePriority} style={styles.flagButton} hitSlop={8}>
        <IconSymbol
          name={highPriority ? 'flag.fill' : 'flag'}
          size={20}
          color={highPriority ? palette.danger : palette.textMuted}
        />
      </Pressable>

      <Pressable
        onPress={() => handleSubmit()}
        disabled={!hasText}
        style={[styles.submitButton, { backgroundColor: palette.primary, opacity: hasText ? 1 : 0.3 }]}
        hitSlop={8}
      >
        <IconSymbol name="plus" size={20} color="#fff" />
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginHorizontal: Spacing.lg,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.lg,
    borderWidth: 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
    overflow: 'hidden',
  },
  flashOverlay: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: BorderRadius.lg,
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    paddingVertical: Spacing.xs,
  },
  flagButton: {
    marginLeft: Spacing.sm,
    padding: Spacing.xs,
  },
  submitButton: {
    marginLeft: Spacing.sm,
    width: 32,
    height: 32,
    borderRadius: BorderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
